import { AuthProvider, AuthProviderProps } from "react-oidc-context";
import { RouterProvider } from "react-router-dom";

import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { ThemeProvider } from "@mui/material/styles";
import {
  AlertContextProvider,
  AuthGuard,
  ConfirmationDialogContextProvider,
  FSW_PORTAL_IDP_ALIAS,
  appTheme,
} from "@shared";

import { RequiredRoleGuard } from "@/common/components/required-role-guard/RequiredRoleGuard";
import CurrentUserContextProvider from "@/common/contexts/current-user-context/CurrentUserContextProvider";
import LanguageContextProvider from "@/common/contexts/language-context/LanguageContextProvider";
import LoadingIndicatorContextProvider from "@/common/contexts/loading-indicator-context/LoadingIndicatorContextProvider";
import LocalizationDateProvider from "@/common/contexts/localization-date-context/LocalizationDateContextProvider";
import { router } from "@/router/RouteConfig";

// hoist the <GlobalStyles /> to a static constant, to avoid rerendering. This will ensure that the <style> tag
// generated would not recalculate on each render.
const inputGlobalStyles = (
  <GlobalStyles
    styles={{
      html: {
        // Needed for percentage heights to work on children
        height: "100%",
        // The app shell is exactly one viewport tall and scrolls internally, so the document itself
        // must never scroll: without this any element that escapes the shell hands the window a
        // second scrollbar next to the one the content area already has.
        overflow: "hidden",
      },
      body: {
        height: "100%",
        overflow: "hidden",
      },
      "#root": {
        // Website Style (Scrolls naturally)
        minHeight: "100%",
        height: "100%",
        width: "100%",
        display: "flex", // Useful if you want to center content or use flex columns
        flexDirection: "column",
      },
    }}
  />
);

interface AppProps {
  oidcConfig: AuthProviderProps;
}

function App({ oidcConfig }: AppProps) {
  return (
    <>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        {inputGlobalStyles}
        <LoadingIndicatorContextProvider>
          <LanguageContextProvider>
            <LocalizationDateProvider>
              <AuthProvider {...oidcConfig}>
                <AuthGuard brokeredIdpAlias={FSW_PORTAL_IDP_ALIAS}>
                  <RequiredRoleGuard>
                    <CurrentUserContextProvider>
                      <AlertContextProvider>
                        <ConfirmationDialogContextProvider>
                          <RouterProvider router={router} />
                        </ConfirmationDialogContextProvider>
                      </AlertContextProvider>
                    </CurrentUserContextProvider>
                  </RequiredRoleGuard>
                </AuthGuard>
              </AuthProvider>
            </LocalizationDateProvider>
          </LanguageContextProvider>
        </LoadingIndicatorContextProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
